// External dependencies.
import React from "react";
import { defineMessages } from "react-intl";

// Internal dependencies.
import {
	HomeIcon,
	ComputerDesktopIcon,
	ArrowDownTrayIcon,
	SquaresPlusIcon,
} from "@heroicons/react/24/outline";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { SitesPageContainer } from "../containers/SitesPage";
import DownloadsPageContainer from "../containers/DownloadsPage";
import SubscriptionPageContainer from "../containers/SubscriptionsPage";
import HomePage from "../components/home/HomePage";
import getEnv from "shared-frontend/functions/getEnv";
import * as styles from "./menu.module.scss";
import OrdersPageContainer from "../containers/OrdersPage";
import ProfilePageContainer from "../containers/ProfilePage";
import BrandInsightsTool from "../components/BrandInsightsTool";
import PersonalAccessTokensPage from "../components/account/personal-access-tokens/PersonalAccessTokensPage";

const titles = defineMessages( {
	home: {
		id: "menu.title.home",
		defaultMessage: "Home",
	},
	sites: {
		id: "menu.title.sites",
		defaultMessage: "Sites",
	},
	subscriptions: {
		id: "menu.title.subscriptions",
		defaultMessage: "Subscriptions",
	},
	courses: {
		id: "menu.title.courses",
		defaultMessage: "Academy",
	},
	downloads: {
		id: "menu.title.downloads",
		defaultMessage: "Downloads",
	},
	account: {
		id: "menu.title.account",
		defaultMessage: "Account",
	},
	order: {
		id: "menu.title.order",
		defaultMessage: "Orders",
	},
	profile: {
		id: "menu.title.profile",
		defaultMessage: "Profile",
	},
	shop: {
		id: "menu.title.shop",
		defaultMessage: "Shop",
	},
	aiBrandInsightsTool: {
		id: "menu.title.aiBrandInsightsTool",
		defaultMessage: "Brand Insights",
	},
	personalAccessTokens: {
		id: "menu.title.personalAccessTokens",
		defaultMessage: "Personal access tokens",
	},
} );

export const aiBrandInsightsPath = "/ai-brand-insights-tool";

const menuItems = [
	{
		showInMenu: true,
		path: "/",
		titleKey: "home",
		iconSource: <HomeIcon />,
		component: HomePage,
		exact: true,
		isActive: ( match, location ) => {
			return location.pathname === "/";
		},
		title: titles.home,
		inMainLayout: true,
		noBackground: true,
		// External link properties
		isExternal: false,
	},
	{
		showInMenu: true,
		path: "/sites",
		titleKey: "sites",
		iconSource: <ComputerDesktopIcon />,
		component: SitesPageContainer,
		exact: true,
		title: titles.sites,
		inMainLayout: true,
		// External link properties
		isExternal: false,
	},
	{
		showInMenu: true,
		path: "/subscriptions",
		titleKey: "subscriptions",
		iconSource: <SquaresPlusIcon />,
		component: SubscriptionPageContainer,
		exact: true,
		title: titles.subscriptions,
		inMainLayout: true,
		// External link properties
		isExternal: false,
	},
	{
		showInMenu: false,
		showInPlatformMenu: true,
		path: "/courses",
		titleKey: "courses",
		component: () => {
			return window.location.replace(
				getEnv( "LEARNDASH_URL", "https://academy.yoast.com" ) + "/courses",
			);
		},
		exact: true,
		title: titles.courses,
		inMainLayout: true,

		// External link properties
		isExternal: true,
		pathname: { pathname: getEnv( "LEARNDASH_URL", "https://academy.yoast.com" ) + "/courses" },
		externalLinkIconSource: <ArrowTopRightOnSquareIcon className={ styles.externalLink } />,
	},
	{
		showInMenu: true,
		path: "/downloads",
		titleKey: "downloads",
		iconSource: <ArrowDownTrayIcon />,
		component: DownloadsPageContainer,
		exact: true,
		title: titles.downloads,
		inMainLayout: true,

		// External link properties
		isExternal: false,
	},
	{
		showInMenu: false,
		showInAccountMenu: true,
		path: "/orders",
		titleKey: "orders",
		component: OrdersPageContainer,
		exact: false,
		title: titles.order,
		inMainLayout: true,

		// External link properties
		isExternal: false,
	},
	{
		showInMenu: false,
		showInAiMenu: true,
		path:
			aiBrandInsightsPath,
		titleKey: "aiBrandInsightsTool",
		component: BrandInsightsTool,
		exact: false,
		title: titles.aiBrandInsightsTool,
		inMainLayout: false,

		// External link properties
		isExternal: true,
		pathname: {
			pathname: aiBrandInsightsPath,
			target: "aiBrandInsights",
		},
		externalLinkIconSource: <ArrowTopRightOnSquareIcon className={ styles.externalLink } />,
	},
	{
		showInMenu: false,
		showInAccountMenu: true,
		path: "/profile",
		titleKey: "profile",
		component: ProfilePageContainer,
		exact: false,
		title: titles.profile,
		inMainLayout: true,

		// External link properties
		isExternal: false,
	},
	{
		showInMenu: false,
		showInAccountMenu: true,
		path: "/personal-access-tokens",
		titleKey: "personalAccessTokens",
		component: PersonalAccessTokensPage,
		exact: false,
		title: titles.personalAccessTokens,
		inMainLayout: true,

		// External link properties
		isExternal: false,
	},
	{
		showInMenu: false,
		showInPlatformMenu: true,
		path: "/shop",
		titleKey: "shop",
		component: () => {
			return window.location.replace(
				getEnv( "SHOP_URL", "https://yoast.com" ) + "/shop/",
			);
		},
		exact: false,
		title: titles.shop,
		inMainLayout: true,

		// External link properties
		isExternal: true,
		pathname: { pathname: getEnv( "SHOP_URL", "https://yoast.com" ) + "/shop/" },
		externalLinkIconSource: <ArrowTopRightOnSquareIcon className={ styles.externalLink } />,
	},
];

export default menuItems;
